import { defineStore } from "pinia"
import {
	type Company,
	type CompanySmsProviderConfig,
	type OrganizationChartNode,
	type OrganizationChartTip
} from "~/models/company"
import type { Log } from "~/models/log"
import type { User } from "~/models/user"
import { type ApiResponse } from "~/types/api"

export const useCompanyStore = defineStore("company", () => {
	const app = useNuxtApp()

	const companyId = useCookie<number | null>("company_id", {
		maxAge: 60 * 60 * 24 * 30 * 2
	})
	const state = {
		companyId: ref(companyId.value),
		company: ref<Company | null>(null),
		payrollDomainCompany: ref<Company | null>(null),
		careerDomainCompany: ref<Company | null>(null)
	}

	const smsProviders = computed(
		() =>
			[
				{
					id: "default",
					name: app.$i18n.t("default"),
					bgClass: "bg-primary",
					pattern: true,
					message: false
				},
				{
					id: "ghasedak",
					name: app.$i18n.t("ghasedak"),
					url: "https://ghasedak.me",
					bgClass: "bg-[#0089ff]",
					pattern: true,
					message: false
				},
				{
					id: "mediapayamak",
					name: app.$i18n.t("mediapayamak"),
					url: "https://mediapayamak.com",
					bgClass: "bg-[#25b1c3]",
					pattern: false,
					message: true
				},
				{
					id: "asanak",
					name: app.$i18n.t("asanak"),
					url: "https://asanak.com/",
					bgClass: "bg-[#fa9c00]",
					pattern: false,
					message: true
				}
			] as const
	)

	const domainCompany = computed(() => {
		if (state.payrollDomainCompany.value) {
			return {
				...state.payrollDomainCompany.value,
				domain: state.payrollDomainCompany.value.payroll_domain,
				homepage: state.payrollDomainCompany.value.payroll_homepage,
				baseurl: state.payrollDomainCompany.value.payroll_baseurl,
				is_domain_ok: state.payrollDomainCompany.value.is_payroll_domain_ok,
				is_change_domain: state.payrollDomainCompany.value.is_change_payroll_domain
			}
		} else if (state.careerDomainCompany.value) {
			return {
				...state.careerDomainCompany.value,
				domain: state.careerDomainCompany.value.career_domain,
				homepage: state.careerDomainCompany.value.career_homepage,
				baseurl: state.careerDomainCompany.value.career_baseurl,
				is_domain_ok: state.careerDomainCompany.value.is_career_domain_ok,
				is_change_domain: state.careerDomainCompany.value.is_change_career_domain
			}
		} else {
			return null
		}
	})

	const setCompany = (id: number | null, company?: Company | null) => {
		companyId.value = id
		state.companyId.value = id
		if (company !== undefined) state.company.value = company ? { ...company } : null
	}

	const fetchCompany = async (force: boolean = false) => {
		const userStore = useUserStore()
		if (state.companyId.value == state.company.value?.id && !force) return
		try {
			if (state.companyId.value) {
				const c = await getCompany(state.companyId.value)
				if (
					!["admin", "support"].includes(userStore.user?.type || "") &&
					c.user_id != userStore.user?.id &&
					c.id != userStore.user?.company_id
				) {
					state.companyId.value = null
					state.company.value = null
				} else {
					state.company.value = c
				}
			} else {
				state.company.value = null
			}
		} catch (e: any) {
			if (e.response._data?.errors?.id?.exist) {
				state.companyId.value = null
				state.company.value = null
			} else {
				throw e
			}
		}
	}

	const createCompany = async (formData: CreateCompanyRequest) => {
		const data = await $fetch<CreateCompanyResponse>(CreateCompany.url(), {
			...useFetchConfig(),
			method: CreateCompany.method,
			body: formData
		})
		return data.data
	}

	const editCompany = async (formData: EditCompanyRequest) => {
		const data = await $fetch<EditCompanyResponse>(EditCompany.url(), {
			...useFetchConfig(),
			method: EditCompany.method,
			body: formData
		})
		if (state.company?.value?.id == data?.data?.id) {
			state.company.value = data.data
		}
		return data.data
	}

	const deleteCompany = async (id: number) => {
		const data = await $fetch<DeleteCompanyResponse>(DeleteCompany.url(id), {
			...useFetchConfig(),
			method: DeleteCompany.method
		})
		return data.data
	}

	const getAllCompanies = async (formData: GetAllCompaniesRequest) => {
		const data = await $fetch<GetAllCompaniesResponse>(GetAllCompanies.url(), {
			...useFetchConfig(),
			method: GetAllCompanies.method,
			query: formData
		})
		return data.data
	}

	const searchCompanies = async (formData: SearchCompaniesRequest) => {
		const data = await $fetch<SearchCompaniesResponse>(SearchCompanies.url(), {
			...useFetchConfig(),
			method: SearchCompanies.method,
			query: formData
		})
		return data.data
	}

	const checkCName = async (formData: CompanyCheckCnameRequest) => {
		const data = await $fetch<CompanyCheckCnameResponse>(CompanyCheckCname.url(), {
			...useFetchConfig(),
			method: CompanyCheckCname.method,
			body: formData
		})
		return data.data
	}

	const getCompany = async (id: number) => {
		const data = await $fetch<GetCompanyResponse>(GetCompany.url(id), {
			...useFetchConfig(),
			method: GetCompany.method
		})
		if (state.companyId.value == id) {
			state.company.value = data.data
		}
		return data.data
	}

	const organizationChart = async (params: OrganizationChartRequest) => {
		const data = await $fetch<OrganizationChartResponse>(OrganizationChart.url(), {
			...useFetchConfig(),
			method: OrganizationChart.method,
			params
		})
		return data.data
	}

	const editSmsProvider = async (formData: CompanyEditSmsProviderRequest) => {
		const data = await $fetch<CompanyEditSmsProviderResponse>(CompanyEditSmsProvider.url(), {
			...useFetchConfig(),
			method: CompanyEditSmsProvider.method,
			body: formData
		})
		return data.data
	}

	const getSmsProvider = async (formData: CompanyGetSmsProviderRequest) => {
		const data = await $fetch<CompanyGetSmsProviderResponse>(CompanyGetSmsProvider.url(), {
			...useFetchConfig(),
			method: CompanyGetSmsProvider.method,
			query: formData
		})
		return data.data
	}

	const getUnseenCounts = async () => {
		const companyIdBefore = state.company.value?.id
		const data = await $fetch<GetUnseenCountsResponse>(GetUnseenCounts.url(), {
			...useFetchConfig(),
			method: GetUnseenCounts.method
		})
		if (state.company.value && state.company.value.id === companyIdBefore) {
			state.company.value = {
				...state.company.value,
				...data.data
			}
		}
		return data.data
	}

	return {
		...state,
		domainCompany,
		smsProviders,
		fetchCompany,
		createCompany,
		editCompany,
		deleteCompany,
		getAllCompanies,
		searchCompanies,
		getCompany,
		setCompany,
		checkCName,
		organizationChart,
		editSmsProvider,
		getSmsProvider,
		getUnseenCounts
	}
})

// company :: create company
export interface CreateCompanyRequest {
	title?: string
	description?: string
	color?: string
	image_id?: number | null
	timezone?: string
	currency_id?: number
	project_allow_file_upload: boolean
	apply_penalty_on_payroll: boolean
	// payroll
	payroll_homepage?: string
	payroll_baseurl?: string
	is_change_payroll_domain?: boolean
	// career
	career_homepage?: string
	career_baseurl?: string
	is_change_career_domain?: boolean
}
export interface CreateCompanyResponse extends ApiResponse {
	data: Company
}
const CreateCompany = {
	method: "POST" as const,
	url: () => `companies/create`
}

// company :: edit company
export interface EditCompanyRequest {
	id: number
	title?: string
	description?: string
	color?: string
	image_id?: number | null
	timezone?: string
	currency_id?: number
	project_allow_file_upload?: boolean
	apply_penalty_on_payroll?: boolean
	notify_payroll?: boolean
	notify_performance_review?: boolean
	notify_registration?: boolean
	notify_birthday?: boolean
	notify_announcement?: boolean
	notify_survey?: boolean
	notify_chat?: boolean
	notify_project_activity?: boolean
	notify_disciplinary_note?: boolean
	penalty_amount?: number
	penalty_apply_point?: number
	// payroll
	payroll_homepage?: string
	payroll_baseurl?: string
	is_change_payroll_domain?: boolean
	// career
	career_homepage?: string
	career_baseurl?: string
	is_change_career_domain?: boolean
	is_vira_enable?: boolean
	is_food_module_enable?: boolean
	is_survey_module_enable?: boolean
	is_benefit_fund_module_enable?: boolean
	is_allowed_to_chat_with_everyone?: boolean
}
export interface EditCompanyResponse extends ApiResponse {
	data: Company
}
const EditCompany = {
	method: "POST" as const,
	url: () => `companies/edit`
}

// company :: delete company
export interface DeleteCompanyRequest {}
export interface DeleteCompanyResponse extends ApiResponse {}
const DeleteCompany = {
	method: "POST" as const,
	url: (id: any) => `companies/delete/${id}`
}

// company :: get all companies
export interface GetAllCompaniesRequest {
	user_id?: number
}
export interface GetAllCompaniesResponse extends ApiResponse {
	data: Company[]
}
const GetAllCompanies = {
	method: "GET" as const,
	url: () => `companies/all`
}

// company :: search companies
export interface SearchCompaniesRequest {
	search?: string
	user?: string
	limit?: number
	page?: number
	order_by?: string
	direction?: string
	except_company_id?: number
	user_id?: number
}
export interface SearchCompaniesResponse extends ApiResponse {
	data: {
		companies: Company[]
		total: number
	}
}
const SearchCompanies = {
	method: "GET" as const,
	url: () => `companies/search`
}

// company :: check cname
export interface CompanyCheckCnameRequest {
	domain: string
	target: string
}
export interface CompanyCheckCnameResponse extends ApiResponse {
	data: {
		domain: string
		target: string
		ok: boolean
	}
}
const CompanyCheckCname = {
	method: "POST" as const,
	url: () => `companies/check-cname`
}

// company :: get company
export interface GetCompanyRequest {}
export interface GetCompanyResponse extends ApiResponse {
	data: Company
}
const GetCompany = {
	method: "GET" as const,
	url: (id: any) => `companies/${id}`
}

// company :: organization chart
export interface OrganizationChartRequest {}
export interface OrganizationChartResponse extends ApiResponse {
	data: {
		nodes: OrganizationChartNode[]
		excluded_users: User[]
		tips: OrganizationChartTip[]
	}
}
const OrganizationChart = {
	method: "GET" as const,
	url: () => `companies/organization-chart`
}

// company :: edit sms provider
export interface CompanyEditSmsProviderRequest {
	id: number
	type: string
	payroll_pattern?: string
	performance_review_pattern?: string
	company_activation_code_pattern?: string
	company_user_registration_pattern?: string
	company_user_birthday_pattern?: string
	announcement_pattern?: string
	disciplinary_note_pattern?: string
	survey_pattern?: string
	chat_pattern?: string
	project_activity_pattern?: string
	payroll_message?: string
	performance_review_message?: string
	company_activation_code_message?: string
	company_user_registration_message?: string
	company_user_birthday_message?: string
	announcement_message?: string
	disciplinary_note_message?: string
	survey_message?: string
	chat_message?: string
	project_activity_message?: string
	number?: string
	api_key?: string
	username?: string
	password?: string
}
export interface CompanyEditSmsProviderResponse extends ApiResponse {
	data: {
		type: string
		config: CompanySmsProviderConfig
		logs: Log[]
		sms_types: {
			id: string
			params: {
				id: string
				name: string
			}[]
		}[]
	}
}
const CompanyEditSmsProvider = {
	method: "POST" as const,
	url: () => `companies/edit-sms-provider`
}

// company :: get sms provider
export interface CompanyGetSmsProviderRequest {
	id: number
}
export interface CompanyGetSmsProviderResponse extends ApiResponse {
	data: {
		type: string
		config: CompanySmsProviderConfig
		logs: Log[]
		sms_types: {
			id: string
			params: {
				id: string
				name: string
			}[]
		}[]
	}
}
const CompanyGetSmsProvider = {
	method: "GET" as const,
	url: () => `companies/get-sms-provider`
}

// company :: get unseen counts
export interface GetUnseenCountsRequest {
	id: number
}
export interface GetUnseenCountsResponse extends ApiResponse {
	data: {
		total_unseen_disciplinary_notes?: number
		total_unseen_announcements?: number
		total_unseen_payrolls?: number
		total_unseen_reward_payrolls?: number
		total_unseen_performance_reviews?: number
		total_unseen_messages?: number
		total_unseen_mails?: number
	}
}
const GetUnseenCounts = {
	method: "GET" as const,
	url: () => `companies/get-unseen-counts`
}

export const useCompanyApiUrl = () => ({
	CreateCompany,
	EditCompany,
	DeleteCompany,
	GetAllCompanies,
	GetCompany,
	OrganizationChart,
	CompanyEditSmsProvider,
	CompanyGetSmsProvider
})
